/**
 * Application constants
 */

// Severity levels
export const SEVERITY_LEVELS = {
  CRITICAL: 'critical',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const;

// Deck status
export const DECK_STATUS = {
  UPLOADED: 'uploaded',
  CONVERTING: 'converting',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

// Slide status
export const SLIDE_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

// Export formats
export const EXPORT_FORMATS = {
  PDF: 'pdf',
  EXCEL: 'excel',
  CSV: 'csv',
} as const;

// Severity colors (matching theme)
export const SEVERITY_COLORS = {
  [SEVERITY_LEVELS.CRITICAL]: '#D3410C',
  [SEVERITY_LEVELS.ERROR]: '#E1502F',
  [SEVERITY_LEVELS.WARNING]: '#FFCF08',
  [SEVERITY_LEVELS.INFO]: '#1C5E99',
} as const;

// File upload constraints
export const FILE_CONSTRAINTS = {
  MAX_SIZE_MB: 50,
  MAX_SIZE_BYTES: 50 * 1024 * 1024,
  ACCEPTED_TYPES: ['.ppt', '.pptx'],
  ACCEPTED_MIME_TYPES: [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
} as const;

// Style guide upload constraints
export const STYLE_GUIDE_CONSTRAINTS = {
  MAX_SIZE_MB: 25,
  MAX_SIZE_BYTES: 25 * 1024 * 1024,
  ACCEPTED_EXTENSIONS: ['.pdf', '.docx', '.doc', '.txt', '.md', '.png', '.jpg', '.jpeg', '.pptx', '.ppt', '.xlsx', '.xls', '.html', '.htm'],
  ACCEPTED_MIME_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
    'image/png',
    'image/jpeg',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/html',
  ],
} as const;

// Attachment constraints
export const ATTACHMENT_CONSTRAINTS = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  MAX_FILES: 5,
  ACCEPTED_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ],
} as const;

// Polling intervals (in milliseconds)
export const POLLING_INTERVALS = {
  STATUS_UPDATE: 3000, // 3 seconds
  CHAT_CHUNK: 100, // 100ms
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [20, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

// Chat
export const CHAT = {
  MAX_MESSAGE_LENGTH: 2000,
  TYPEWRITER_DELAY_MS: 2,
  MAX_ATTACHMENTS: 5,
} as const;

// Analysis tasks (default list - loaded from API)
export const DEFAULT_TASKS = [
  {
    taskId: 'spelling-grammar',
    name: 'Spelling & Grammar Check',
    description: 'Identifies spelling mistakes and grammatical errors',
    estimatedTime: 'fast' as const,
    defaultEnabled: true,
  },
  {
    taskId: 'incomplete-info',
    name: 'Incomplete Information',
    description: 'Detects slides with missing or incomplete content',
    estimatedTime: 'medium' as const,
    defaultEnabled: true,
  },
  {
    taskId: 'title-content-mismatch',
    name: 'Title/Content Mismatch',
    description: 'Checks if slide titles accurately reflect content',
    estimatedTime: 'medium' as const,
    defaultEnabled: true,
  },
  {
    taskId: 'chart-issues',
    name: 'Chart Visual Issues',
    description: 'Analyzes charts for clarity, labeling, and visual problems',
    estimatedTime: 'slow' as const,
    defaultEnabled: true,
  },
  {
    taskId: 'visual-discrepancies',
    name: 'Visual Discrepancies',
    description: 'Identifies formatting inconsistencies and visual issues',
    estimatedTime: 'medium' as const,
    defaultEnabled: true,
  },
  {
    taskId: 'style-guide-adherence',
    name: 'Style Guide Adherence',
    description: 'Compares slides against your uploaded style guide to detect visual and formatting variances',
    estimatedTime: 'slow' as const,
    defaultEnabled: false,
    requiresStyleGuide: true,
  },
] as const;

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'bri_token',
  REFRESH_TOKEN: 'bri_refresh_token',
  USER_INFO: 'bri_user_info',
  SAVED_AUDIENCE: 'bri_saved_audience',
  LAST_DECK_ID: 'last_deck_id',
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  UPLOAD: '/upload',
  DECKS: '/decks',
  ANALYSIS: '/analysis/:deckId',
  SLIDE_DETAIL: '/analysis/:deckId/slide/:slideNumber',
  CHAT: '/chat/:deckId',
  AUTH_CALLBACK: '/auth/callback',
} as const;

// Convenience exports for file upload
export const MAX_FILE_SIZE = FILE_CONSTRAINTS.MAX_SIZE_BYTES;
export const ACCEPTED_FILE_TYPES = FILE_CONSTRAINTS.ACCEPTED_TYPES;

